import * as React from "react"
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react"
import { DayButton, DayPicker, getDefaultClassNames, type Locale } from "react-day-picker"

import { Button, buttonVariants } from "../ui/button"
import { cn } from "../../utils/cn"

interface CustomDropdownProps {
  value: number
  onChange: (value: number) => void
  options: Array<{ label: string; value: number }>
  className?: string
}

function CustomDropdown({ value, onChange, options, className }: CustomDropdownProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const dropdownListRef = React.useRef<HTMLDivElement>(null)
  const selectedButtonRef = React.useRef<HTMLButtonElement>(null)

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  // Scroll to selected option when dropdown opens
  React.useEffect(() => {
    if (isOpen && selectedButtonRef.current && dropdownListRef.current) {
      const button = selectedButtonRef.current
      const container = dropdownListRef.current

      // Calculate position to center the selected item
      const buttonTop = button.offsetTop
      const buttonHeight = button.offsetHeight
      const containerHeight = container.clientHeight

      // Scroll to center the selected item
      container.scrollTop = buttonTop - (containerHeight / 2) + (buttonHeight / 2)
    }
  }, [isOpen])

  const selectedOption = options.find((opt) => opt.value === value)

  return (
    <div ref={dropdownRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-1 rounded-md border border-input shadow-xs h-8 px-2 text-sm font-medium",
          "hover:bg-accent hover:text-accent-foreground",
          "focus:border-ring focus:ring-ring/50 focus:ring-[3px] focus:outline-none",
          "transition-colors"
        )}
      >
        <span>{selectedOption?.label}</span>
        <ChevronDownIcon
          className={cn(
            "size-3.5 text-muted-foreground transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </button>
      {isOpen && (
        <div
          ref={dropdownListRef}
          className={cn(
            "absolute top-full left-0 mt-1 z-50",
            "bg-white border border-border rounded-md shadow-md",
            "max-h-[200px] overflow-y-auto",
            "min-w-[100px]",
            "scroll-smooth"
          )}
        >
          {options.map((option) => (
            <button
              key={option.value}
              ref={value === option.value ? selectedButtonRef : null}
              type="button"
              onClick={() => {
                onChange(option.value)
                setIsOpen(false)
              }}
              className={cn(
                "w-full text-left px-3 py-2 text-sm",
                "hover:bg-accent hover:text-accent-foreground",
                "transition-colors cursor-pointer",
                value === option.value && "bg-accent text-accent-foreground font-medium"
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

interface MonthYearDropdownsProps {
  displayMonth: Date
  onMonthChange?: (date: Date) => void
  fromYear?: number
  toYear?: number
  locale?: Partial<Locale>
}

function MonthYearDropdowns({
  displayMonth,
  onMonthChange,
  fromYear = 1900,
  toYear = new Date().getFullYear() + 10,
  locale,
}: MonthYearDropdownsProps) {
  const months = Array.from({ length: 12 }, (_, i) => {
    const date = new Date(2000, i, 1)
    return {
      label: date.toLocaleString(locale?.code || "default", { month: "short" }),
      value: i,
    }
  })

  const years = Array.from({ length: toYear - fromYear + 1 }, (_, i) => ({
    label: String(fromYear + i),
    value: fromYear + i,
  })).reverse()

  const handleMonthChange = (month: number) => {
    if (onMonthChange) {
      const newDate = new Date(displayMonth.getFullYear(), month, 1)
      onMonthChange(newDate)
    }
  }

  const handleYearChange = (year: number) => {
    if (onMonthChange) {
      const newDate = new Date(year, displayMonth.getMonth(), 1)
      onMonthChange(newDate)
    }
  }

  return (
    <div className="flex items-center justify-center gap-1.5 h-(--cell-size) w-full px-(--cell-size)">
      <CustomDropdown
        value={displayMonth.getMonth()}
        onChange={handleMonthChange}
        options={months}
      />
      <CustomDropdown
        value={displayMonth.getFullYear()}
        onChange={handleYearChange}
        options={years}
      />
    </div>
  )
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  formatters,
  components,
  fromYear,
  toYear,
  locale,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"]
}) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "bg-white group/calendar p-3 [--cell-size:--spacing(8)] [[data-slot=card-content]_&]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString("default", { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),
        months: cn(
          "flex gap-4 flex-col md:flex-row relative",
          defaultClassNames.months
        ),
        month: cn("flex flex-col w-full gap-4", defaultClassNames.month),
        nav: cn(
          "flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between",
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) aria-disabled:opacity-50 p-0 select-none",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) aria-disabled:opacity-50 p-0 select-none",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "flex items-center justify-center h-(--cell-size) w-full px-(--cell-size)",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "w-full flex items-center text-sm font-medium justify-center h-(--cell-size) gap-1.5",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          "relative has-focus:border-ring border border-input shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] rounded-md",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(
          "absolute inset-0 w-full appearance-none opacity-0 cursor-pointer z-10",
          defaultClassNames.dropdown
        ),
        caption_label: cn(
          "select-none font-medium",
          captionLayout === "label"
            ? "text-sm"
            : "rounded-md pl-2 pr-1 flex items-center gap-1 text-sm h-8 [&>svg]:text-muted-foreground [&>svg]:size-3.5",
          defaultClassNames.caption_label
        ),
        table: "w-full border-collapse",
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          "text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem] select-none",
          defaultClassNames.weekday
        ),
        week: cn("flex w-full mt-2", defaultClassNames.week),
        week_number_header: cn(
          "select-none w-(--cell-size)",
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          "text-[0.8rem] select-none text-muted-foreground",
          defaultClassNames.week_number
        ),
        day: cn(
          "relative w-full h-full text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md group/day aspect-square select-none",
          defaultClassNames.day
        ),
        range_start: cn(
          "rounded-l-md bg-accent",
          defaultClassNames.range_start
        ),
        range_middle: cn("rounded-none", defaultClassNames.range_middle),
        range_end: cn("rounded-r-md bg-accent", defaultClassNames.range_end),
        today: cn(
          "bg-accent text-accent-foreground rounded-md data-[selected=true]:rounded-none",
          defaultClassNames.today
        ),
        outside: cn(
          "text-muted-foreground aria-selected:text-muted-foreground",
          defaultClassNames.outside
        ),
        disabled: cn(
          "text-muted-foreground opacity-50",
          defaultClassNames.disabled
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          )
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <ChevronLeftIcon className={cn("size-4", className)} {...props} />
            )
          }

          if (orientation === "right") {
            return (
              <ChevronRightIcon
                className={cn("size-4", className)}
                {...props}
              />
            )
          }

          return (
            <ChevronDownIcon className={cn("size-4", className)} {...props} />
          )
        },
        MonthCaption: (monthCaptionProps) => {
          if (captionLayout === "dropdown") {
            return (
              <MonthYearDropdowns
                displayMonth={monthCaptionProps.calendarMonth.date}
                onMonthChange={props.onMonthChange}
                fromYear={fromYear}
                toYear={toYear}
                locale={locale}
              />
            )
          }
          return <></>
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>
          )
        },
        ...components,
      }}
      fromYear={fromYear}
      toYear={toYear}
      locale={locale}
      {...props}
    />
  )
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames()

  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        "data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50 dark:hover:text-accent-foreground flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 leading-none font-normal group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md data-[range-start=true]:rounded-l-md [&>span]:text-xs [&>span]:opacity-70",
        defaultClassNames.day,
        className
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
